import { ChangeEvent, FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createCustomer,
  fetchCustomer,
  updateCustomer,
} from '../api/customers';
import {
  createCustomerVehicle,
  fetchCustomerVehicles,
  updateCustomerVehicle,
  CustomerVehicle,
} from '../api/customerVehicles';
import CustomerVehicleModal from '../components/CustomerVehicleModal';
import PageHeaderWithBack from '../components/PageHeaderWithBack';
import { Country, fetchAllCountries } from '../api/countries';
import SearchableCountrySelect from '../components/SearchableCountrySelect';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  countryCode: string;
  dateOfBirth: string;
}

interface VehicleFormState {
  vehicleBrandId: string;
  vehicleModelId: string;
  vehicleRegistrationYear: string;
  vehicleRegistrationNumber: string;
  vehicleVin: string;
  vehicleChassisNumber: string;
  fuelType: string[];
  customerId: string;
}

const buildInitialForm = (): FormState => ({
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
  countryCode: '60',
  dateOfBirth: '',
});

const buildVehicleForm = (customerId: string): VehicleFormState => ({
  vehicleBrandId: '',
  vehicleModelId: '',
  vehicleRegistrationYear: '',
  vehicleRegistrationNumber: '',
  vehicleVin: '',
  vehicleChassisNumber: '',
  fuelType: [],
  customerId,
});

const normalizeNumberInput = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const numeric = Number(trimmed);
  if (Number.isNaN(numeric)) {
    return null;
  }
  return numeric;
};

const CustomerEditor: FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId?: string }>();
  const isEdit = Boolean(customerId && customerId !== 'add');

  const [formState, setFormState] = useState<FormState>(buildInitialForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Countries for dynamic phone code selector
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  // searchable handled inside custom dropdown

  useEffect(() => {
    let active = true;
    setCountriesLoading(true);
    setCountriesError(null);
    fetchAllCountries()
      .then((data) => {
        if (!active) return;
        setCountries((data || []).filter((c) => Boolean(c.phoneCode)));
      })
      .catch((err) => {
        if (!active) return;
        const message = err instanceof Error ? err.message : 'Unable to load countries.';
        setCountriesError(message);
      })
      .finally(() => {
        if (active) setCountriesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);

  const [vehicleFormOpen, setVehicleFormOpen] = useState(false);
  const [vehicleFormState, setVehicleFormState] = useState<VehicleFormState | null>(null);
  const [vehicleFormSaving, setVehicleFormSaving] = useState(false);
  const [vehicleFormError, setVehicleFormError] = useState<string | null>(null);
  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !customerId) {
      setFormState(buildInitialForm());
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchCustomer(customerId)
      .then((customer) => {
        if (!isMounted) {
          return;
        }
        // Prefer explicit countryCode from API (phone_code digits) and treat mobileNumber as local
        const explicitCountry = (customer as any).countryCode ?? null;
        const full = customer.mobileNumber ?? '';
        let derivedCountry = explicitCountry || '60';
        let local = full;
        // If no explicit phone code, attempt to infer from a combined number
        if (!explicitCountry) {
          if (countries.length) {
            const match = [...countries]
              .filter((c) => c.phoneCode)
              .sort((a, b) => (b.phoneCode!.length - a.phoneCode!.length))
              .find((c) => full.startsWith(c.phoneCode!));
            if (match?.phoneCode) {
              derivedCountry = match.phoneCode;
              local = full.slice(match.phoneCode.length);
            }
          } else {
            const known = ['60', '65', '91'];
            const m = known.find((cc) => full.startsWith(cc));
            if (m) {
              derivedCountry = m;
              local = full.slice(m.length);
            }
          }
        }

        setFormState({
          firstName: customer.firstName ?? '',
          lastName: customer.lastName ?? '',
          email: customer.email ?? '',
          mobileNumber: local,
          countryCode: derivedCountry,
          dateOfBirth: customer.dateOfBirth || '',
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Unable to load customer.';
        setError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [customerId, isEdit]);

  const loadVehicles = useCallback(() => {
    if (!customerId || !isEdit) {
      setVehicles([]);
      return;
    }

    let active = true;
    setVehiclesLoading(true);
    setVehiclesError(null);

    fetchCustomerVehicles({ customerId })
      .then((data) => {
        if (active) {
          setVehicles(data);
        }
      })
      .catch((err) => {
        if (active) {
          const message = err instanceof Error ? err.message : 'Unable to load vehicles.';
          setVehiclesError(message);
        }
      })
      .finally(() => {
        if (active) {
          setVehiclesLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [customerId, isEdit]);

  useEffect(() => {
    const dispose = loadVehicles();
    return () => {
      dispose?.();
    };
  }, [loadVehicles]);

  const handleChange = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    // Client-side validation
    const firstName = formState.firstName.trim();
    const lastName = formState.lastName.trim();
    const email = formState.email.trim();
    const localMobile = formState.mobileNumber.trim().replace(/\D/g, '');
    const country = formState.countryCode.trim().replace(/\D/g, '') || '60';
    // Send local number only; phone code is sent separately
    const mobile = `${localMobile}`;
    const dob = formState.dateOfBirth.trim();

    // Reset error before validation to ensure clean state
    setError(null);

    if (!firstName) {
      setError('First name is required.');
      return;
    }
    // Last name is now optional

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('A valid email is required.');
      return;
    }
    if (!/^\d{8,12}$/.test(localMobile)) {
      setError('Phone must be 8–12 digits (excluding country code).');
      return;
    }
    if (!dob) {
      setError('Date of birth is required.');
      return;
    }

    // Validation passed, start saving
    setSaving(true);

    // Determine ISO country code (2/3 chars) from selected phone code
    const selected = countries.find((c) => c.phoneCode === country);
    const payload = {
      firstName,
      lastName,
      email,
      mobileNumber: mobile,
      dateOfBirth: dob,
      countryCode: selected?.phoneCode || undefined,
    };

    try {
      if (isEdit && customerId) {
        await updateCustomer(customerId, payload);
      } else {
        await createCustomer(payload);
      }

      navigate('/crm', { replace: true, state: { refreshed: true } });
    } catch (err) {
      console.error('Customer save error:', err);
      const message = err instanceof Error ? err.message : 'Unable to save customer.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddVehicle = () => {
    if (!customerId) {
      return;
    }
    setVehicleFormState(buildVehicleForm(customerId));
    setVehicleFormError(null);
    setVehicleFormSaving(false);
    setEditingVehicleId(null);
    setVehicleFormOpen(true);
  };

  const handleOpenEditVehicle = (vehicle: CustomerVehicle) => {
    setVehicleFormState({
      customerId: vehicle.customerId ?? customerId ?? '',
      vehicleBrandId: vehicle.vehicleBrandId ?? '',
      vehicleModelId: vehicle.vehicleModelId ?? '',
      vehicleRegistrationYear: vehicle.vehicleRegistrationYear?.toString() ?? '',
      vehicleRegistrationNumber: vehicle.vehicleRegistrationNumber ?? '',
      vehicleVin: vehicle.vehicleVin ?? '',
      vehicleChassisNumber: vehicle.vehicleChassisNumber ?? '',
      fuelType: typeof vehicle.fuelType === 'string' ? vehicle.fuelType.split(',').filter(Boolean) : [],
    });
    setVehicleFormError(null);
    setVehicleFormSaving(false);
    setEditingVehicleId(vehicle.id);
    setVehicleFormOpen(true);
  };

  const handleVehicleFormChange = (field: keyof VehicleFormState, value: string | string[]) => {
    setVehicleFormState((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleVehicleFormSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (!vehicleFormState || vehicleFormSaving) {
      return;
    }

    const registrationNumber = vehicleFormState.vehicleRegistrationNumber.trim();
    if (!registrationNumber) {
      setVehicleFormError('Vehicle registration number is required.');
      return;
    }

    const ownerId = vehicleFormState.customerId.trim();
    if (!ownerId) {
      setVehicleFormError('Provide a customer ID to link this vehicle.');
      return;
    }

    const basePayload = {
      customerId: ownerId,
      vehicleBrandId: vehicleFormState.vehicleBrandId || null,
      vehicleModelId: vehicleFormState.vehicleModelId || null,
      vehicleRegistrationYear: normalizeNumberInput(vehicleFormState.vehicleRegistrationYear),
      vehicleRegistrationNumber: registrationNumber,
      vehicleVin: vehicleFormState.vehicleVin.trim() || null,
      vehicleChassisNumber: vehicleFormState.vehicleChassisNumber.trim() || null,
      fuelType: Array.isArray(vehicleFormState.fuelType)
        ? (vehicleFormState.fuelType.length > 0 ? vehicleFormState.fuelType.join(',') : null)
        : (vehicleFormState.fuelType || null),
    };

    setVehicleFormSaving(true);
    setVehicleFormError(null);

    try {
      if (editingVehicleId) {
        await updateCustomerVehicle(editingVehicleId, basePayload);
      } else {
        const createPayload: Parameters<typeof createCustomerVehicle>[0] = {
          customerId: ownerId,
          vehicleRegistrationNumber: registrationNumber,
          vehicleBrandId: basePayload.vehicleBrandId ?? null,
          vehicleModelId: basePayload.vehicleModelId ?? null,
          vehicleRegistrationYear: basePayload.vehicleRegistrationYear ?? null,
          vehicleVin: basePayload.vehicleVin ?? null,
          vehicleChassisNumber: basePayload.vehicleChassisNumber ?? null,
          fuelType: basePayload.fuelType ?? null,
        };
        await createCustomerVehicle(createPayload);
      }

      setVehicleFormOpen(false);
      setVehicleFormState(null);
      setEditingVehicleId(null);
      loadVehicles();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save vehicle.';
      setVehicleFormError(message);
    } finally {
      setVehicleFormSaving(false);
    }
  };

  const renderVehicleOwner = (vehicle: CustomerVehicle) => {
    if (!vehicle.customer) {
      return '—';
    }
    const name = `${vehicle.customer.firstName ?? ''} ${vehicle.customer.lastName ?? ''}`.trim();
    return name || vehicle.customer.mobileNumber || vehicle.customer.id;
  };

  const vehicleFormTitle = editingVehicleId ? 'Edit vehicle' : 'Add vehicle';

  const vehicleListEmptyMessage = useMemo(() => {
    if (vehiclesLoading) {
      return 'Loading vehicles…';
    }
    if (vehiclesError) {
      return vehiclesError;
    }
    return 'No vehicles linked yet.';
  }, [vehiclesError, vehiclesLoading]);

  const canSubmit = !loading && !saving &&
    Boolean(formState.firstName && formState.lastName && formState.email && formState.mobileNumber && formState.dateOfBirth);

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <PageHeaderWithBack
        title={isEdit ? 'Edit customer' : 'Add'}
        description={<p className="mt-2 max-w-xl text-sm text-slate-500">
          Maintain the core customer profile. Extended attributes and 360° history will arrive soon.
        </p>}
        onBack={() => navigate('/crm')}
        backLabel="Back to customers"
      />


      <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span>First name <span className="text-red-500">*</span></span>
            <input
              type="text"
              value={formState.firstName}
              onChange={handleChange('firstName')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={loading || saving}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span>Last name</span>
            <input
              type="text"
              value={formState.lastName}
              onChange={handleChange('lastName')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={loading || saving}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span>Email <span className="text-red-500">*</span></span>
            <input
              type="email"
              value={formState.email}
              onChange={handleChange('email')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={loading || saving}
            />
          </label>
          <div className="flex flex-col gap-1 text-sm text-slate-600">
            <span>Mobile number <span className="text-red-500">*</span></span>
            <div className="flex gap-2">
              <SearchableCountrySelect
                countries={countries}
                value={formState.countryCode}
                onChange={(code) => setFormState((prev) => ({ ...prev, countryCode: code }))}
                disabled={loading || saving}
                loading={countriesLoading}
                error={countriesError}
              />
              <input
                type="tel"
                value={formState.mobileNumber}
                onChange={handleChange('mobileNumber')}
                placeholder="Local number"
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                disabled={loading || saving}
              />
            </div>
          </div>
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span>Date of birth <span className="text-red-500">*</span></span>
            <input
              type="date"
              value={formState.dateOfBirth}
              onChange={handleChange('dateOfBirth')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={loading || saving}
            />
          </label>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex items-center gap-3 mt-2">
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={loading || saving}
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add'}
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => navigate('/crm')}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>

      {isEdit && customerId ? (
        <section className="mt-10 space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Vehicles</h2>
              <p className="text-xs text-slate-500">Manage the vehicles linked to this customer.</p>
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-semibold bg-primary text-white transition hover:bg-primary/10"
              onClick={handleOpenAddVehicle}
            >
              + Add
            </button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200">
            {vehicles.length === 0 ? (
              <div className="px-5 py-6 text-center text-sm text-slate-500">
                {vehicleListEmptyMessage}
              </div>
            ) : (
              <table className="min-w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Registration #</th>
                    <th className="px-5 py-3 font-semibold">Brand</th>
                    <th className="px-5 py-3 font-semibold">Model</th>
                    <th className="px-5 py-3 font-semibold">Year</th>
                    <th className="px-5 py-3 font-semibold">VIN</th>
                    <th className="px-5 py-3 font-semibold">Fuel</th>
                    <th className="px-5 py-3 font-semibold text-center">Status</th>
                    <th className="px-5 py-3 font-semibold">Linked to</th>
                    <th className="px-5 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="even:bg-slate-50/60">
                      <td className="px-5 py-3 font-medium text-slate-700">{vehicle.vehicleRegistrationNumber}</td>
                      <td className="px-5 py-3 text-slate-500">{vehicle.vehicleBrand?.name ?? '—'}</td>
                      <td className="px-5 py-3 text-slate-500">{vehicle.vehicleModel?.name ?? '—'}</td>
                      <td className="px-5 py-3 text-slate-500">{vehicle.vehicleRegistrationYear ?? '—'}</td>
                      <td className="px-5 py-3 text-slate-500">{vehicle.vehicleVin ?? '—'}</td>
                      <td className="px-5 py-3 text-slate-500">
                        {vehicle.fuelType
                          ? vehicle.fuelType.split(',').map(type => {
                            if (type === 'petrol_hybrid') return 'Petrol Hybrid';
                            return type.toUpperCase();
                          }).join(', ')
                          : '—'}
                      </td>
                      <td className="px-5 py-3 text-center text-slate-700">
                        {vehicle.isActive ? 'Active' : 'Inactive'}
                      </td>
                      <td className="px-5 py-3 text-slate-500">{renderVehicleOwner(vehicle)}</td>
                      <td className="px-5 py-3 text-right">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                          onClick={() => handleOpenEditVehicle(vehicle)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <CustomerVehicleModal
            open={Boolean(vehicleFormOpen && vehicleFormState)}
            title={vehicleFormTitle}
            vehicleBrandId={vehicleFormState?.vehicleBrandId ?? ''}
            vehicleModelId={vehicleFormState?.vehicleModelId ?? ''}
            vehicleRegistrationYear={vehicleFormState?.vehicleRegistrationYear ?? ''}
            vehicleRegistrationNumber={vehicleFormState?.vehicleRegistrationNumber ?? ''}
            vehicleVin={vehicleFormState?.vehicleVin ?? ''}
            vehicleChassisNumber={vehicleFormState?.vehicleChassisNumber ?? ''}
            fuelType={Array.isArray(vehicleFormState?.fuelType) ? vehicleFormState.fuelType : []}
            onChange={(field, value) =>
              vehicleFormState && handleVehicleFormChange(field, value)
            }
            onClose={() => {
              setVehicleFormOpen(false);
              setVehicleFormState(null);
              setEditingVehicleId(null);
              setVehicleFormError(null);
            }}
            onSubmit={() => {
              const fakeFormEvent = { preventDefault: () => undefined } as unknown as FormEvent<HTMLFormElement>;
              handleVehicleFormSubmit(fakeFormEvent);
            }}
            disabled={vehicleFormSaving}
            formError={vehicleFormError}
            customerId={vehicleFormState?.customerId ?? ''}
            showCustomerField={Boolean(editingVehicleId)}
            onCustomerChange={(value) => handleVehicleFormChange('customerId', value)}
          />
        </section>
      ) : null}
    </div>
  );
};

export default CustomerEditor;
